import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth } from "../../http";
import { AddressTypeEnum, ChannelTypeEnum, IdentifierTypeEnum } from "../common/commonTypes";

export type CounterpartyRoleTypeEnum =
    | 'CUSTOMER'
    | 'SUPPLIER'
    | 'PARTNER'
    | 'REPRESENTATIVE'
    | 'SUBCONTRACTOR'
    | 'OTHER';

export type CounterpartyTagTypeEnum =
    | 'VIP'
    | 'BLACKLIST'
    | 'STRATEGIC'
    | 'OTHER';

export interface CounterpartyListItem {
    publicId: string;
    displayName: string;
    countryCode?: string | null;
    accountManagerFullName?: string | null;
    isVip: boolean;
    primaryEmail?: string | null;
    primaryPhone?: string | null;
    website?: string | null;
    isArchived: boolean;
    tags: CounterpartyTagTypeEnum[];
    roles: CounterpartyRoleTypeEnum[];
}

export interface CounterpartyDetails {
    publicId: string;
    displayName: string;
    legalName?: string | null;
    countryCode?: string | null;
    accountManagerFullName?: string | null;
    website?: string | null;
    notes?: string | null;
    isArchived: boolean;
    roles: CounterpartyRoleTypeEnum[];
    tags: CounterpartyTagTypeEnum[];
    identifiers: CounterpartyIdentifier[];
    addresses: CounterpartyAddress[];
    channels: CounterpartyChannel[];
    people: CounterpartyPerson[];
}

export interface CounterpartyIdentifier {
    id: number;
    identifierType: IdentifierTypeEnum;
    countryCode?: string | null;
    registerCode?: string | null;
    value: string;
    isPrimary: boolean;
}

export interface CounterpartyAddress {
    id: number;
    addressType: AddressTypeEnum;
    isPrimary: boolean;
    countryCode: string;
    region?: string | null;
    city?: string | null;
    postalCode?: string | null;
    street?: string | null;
    buildingNo?: string | null;
    apartmentNo?: string | null;
    line1?: string | null;
    line2?: string | null;
}

export interface CounterpartyChannel {
    id: number;
    channelType: ChannelTypeEnum;
    value: string;
    isPrimary: boolean;
}

export interface CounterpartyPersonChannel {
    id: number;
    channelType: ChannelTypeEnum;
    value: string;
    isPrimary: boolean;
}

export interface CounterpartyPerson {
    id: number;
    firstName: string;
    lastName: string;
    jobTitle?: string | null;
    department?: string | null;
    notes?: string | null;
    isPrimary: boolean;

    channels: CounterpartyPersonChannel[];
}

export const loadCounterparties = createAsyncThunk<
    CounterpartyListItem[],
    void,
    AppThunkAPIType
>(
    "counterparties/loadAll",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/counterparties` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CounterpartyListItem[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadCounterpartyDetails = createAsyncThunk<
    CounterpartyDetails,
    { publicId: string },
    AppThunkAPIType
>(
    "counterparties/loadDetails",
    async ({ publicId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            getWithAuth({ url: `api/counterparties/${publicId}` })
        );

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CounterpartyDetails;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);